import { useCallback } from 'react';

import clsx from 'clsx';
import type { RpcState } from 'types/redux';

import type { Note } from '@flow/libs/data-validators/entity-schemas/contract';
import { checkAcl } from '@flow/libs/utils/acl';

import ActionsMenu from '@app/components/actions-menu';
import ParticipantBubble from '@app/components/contract-card/participant-bubble';
import { LiteRichTextViewer } from '@app/components/lite-rich-text';
import { LocalizedDateTime } from '@app/components/localized-date-time';
import { DeleteMenuItem } from '@app/components/menu-items/delete';
import { RemoveConfirm } from '@app/components/modals/remove-confirm';
import { PositionTooltip } from '@app/components/position-tooltip';
import { T } from '@app/components/translation';

import style from './contract-note.module.scss';

type Props = {
  note: Note;
  removeState: RpcState;
  removeNote: () => void;
  resetRemoveState: () => void;
};

export const ContractNote = ({ note, removeState, removeNote, resetRemoveState }: Props) => {
  const { name, content, acl } = note;
  const { nodes } = content;

  const isAllowedToRemoveNote = checkAcl(acl, 'note:remove');
  /* 
    TODO: Add update note action

    const isAllowedToUpdateNote = checkAcl(acl, 'note:update');
  */

  const renderTitle = () => {
    if (!name) {
      return;
    }

    return <div className={style.Title}>{name}</div>;
  };

  const renderContent = () => (
    <div className={clsx(style.Content)}>
      <LiteRichTextViewer
        nodes={nodes}
        elementClassNames={{
          editable: style.ContractNoteContent,
        }}
      />
    </div>
  );

  const renderAuthorInfo = () => {
    const { createdBy, createdTime, updatedBy, updatedTime } = note;

    let author = createdBy;
    let time = createdTime;
    let label = (
      <T
        _str="Created"
        _comment="Label for note created date"
      />
    );

    if (updatedBy && updatedTime) {
      author = updatedBy;
      time = updatedTime;
      label = (
        <T
          _str="Updated"
          _comment="Label for note updated date"
        />
      );
    }

    return (
      <div className={style.MetaDataContainer}>
        <div className={style.DateContainer}>
          {label}
          <LocalizedDateTime datetime={time} />
        </div>
        <PositionTooltip position={author}>
          <span>
            <ParticipantBubble
              participant={author}
              userBadgeDiameter={32}
              className={style.ParticipantBubble}
            />
          </span>
        </PositionTooltip>
      </div>
    );
  };

  /* 
    TODO: Add update note action

    const getUpdateNoteMenuItem = useCallback(
      (onClick: () => void) => <EditMenuItem disabled={!isAllowedToUpdateNote} onClick={onClick} />,
      [isAllowedToUpdateNote],
    );
  */

  const getRemoveNoteMenuItem = useCallback(
    (onClick: () => void) => (
      <DeleteMenuItem
        disabled={!isAllowedToRemoveNote}
        onClick={onClick}
      />
    ),
    [
      isAllowedToRemoveNote,
    ],
  );

  /* 
    TODO: Add update note action

    <RemoveConfirm
      key="update-note-modal"
      modalKey="update-note-modal"
      confirmMessage={
        <T
          _str="Are you sure you want to remove this note?"
          _comment="Confirmation message in modal when attempting to remove a note from contract"
        />
      }
      confirmState={removeState}
      onConfirm={removeNote}
      resetConfirmState={resetRemoveState}
    >
      {getUpdateNoteMenuItem}
    </RemoveConfirm>,
  */

  const renderActionMenu = () => {
    const actions = [
      <RemoveConfirm
        key="remove-note-modal"
        modalKey="remove-note-modal"
        confirmMessage={
          <T
            _str="Are you sure you want to remove this note?"
            _comment="Confirmation message in modal when attempting to remove a note from contract"
          />
        }
        confirmState={removeState}
        onConfirm={removeNote}
        resetConfirmState={resetRemoveState}
      >
        {getRemoveNoteMenuItem}
      </RemoveConfirm>,
    ];

    return (
      <ActionsMenu
        actions={actions}
        focusOnCloseDisabled
        customClassName={style.ActionsMenu}
      />
    );
  };

  return (
    <div className={style.NoteContainer}>
      <div className={style.NoteContent}>
        {renderTitle()}
        {renderContent()}
      </div>
      <div className={style.NoteMetaData}>
        {renderAuthorInfo()}
        {renderActionMenu()}
      </div>
    </div>
  );
};
